import React, { useMemo, useState } from 'react';

import { Sparkline } from './components/Sparkline';
import {
  ITEM_TYPES,
  ITEMS,
  type ItemType,
  formatHotkeyLabel,
  CONTROLS,
} from './constants';
import type { ItemProductionHistoryEntry } from './GameState';
import { secondsToTicks } from './utils/TickIntervals';

import './production-menu.css';

const WINDOW_OPTIONS = [1, 5, 10] as const;
const SEGMENT_COUNT: number = 24;
const TICKS_PER_MINUTE = secondsToTicks(60);

const ITEM_NAME_BY_TYPE: Record<ItemType, string> = Object.values(ITEMS).reduce(
  (acc, item) => {
    acc[item.type] = item.name;
    return acc;
  },
  {} as Record<ItemType, string>
);

type WindowOption = (typeof WINDOW_OPTIONS)[number];

interface ProductionMenuProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly history: ItemProductionHistoryEntry[];
  readonly currentTick: number;
}

interface ItemProductionMetrics {
  readonly rates: number[];
  readonly averagePerMinute: number;
  readonly totalAmount: number;
}

const formatRate = (value: number): string => {
  if (value <= 0) {
    return '0';
  }

  if (value < 0.1) {
    return value.toFixed(3);
  }

  if (value < 1) {
    return value.toFixed(2);
  }

  if (value < 10) {
    return value.toFixed(1);
  }

  return value.toFixed(0);
};

const ProductionMenu: React.FC<ProductionMenuProps> = ({
  isOpen,
  onClose,
  history,
  currentTick,
}) => {
  const [selectedWindow, setSelectedWindow] = useState<WindowOption>(1);

  const metricsByItem = useMemo(() => {
    const windowTicks = secondsToTicks(selectedWindow * 60);
    const windowStartTick = Math.max(0, currentTick - windowTicks);
    const segmentDurationTicks = Math.max(windowTicks / SEGMENT_COUNT, 1);

    const rates: Record<ItemType, number[]> = ITEM_TYPES.reduce(
      (acc, item) => {
        acc[item] = Array.from({ length: SEGMENT_COUNT }, () => 0);
        return acc;
      },
      {} as Record<ItemType, number[]>
    );

    const totals: Record<ItemType, number> = ITEM_TYPES.reduce(
      (acc, item) => {
        acc[item] = 0;
        return acc;
      },
      {} as Record<ItemType, number>
    );

    history.forEach(entry => {
      if (entry.tick < windowStartTick) {
        return;
      }

      const rawIndex = (entry.tick - windowStartTick) / segmentDurationTicks;
      const segmentIndex = Math.min(
        SEGMENT_COUNT - 1,
        Math.max(0, Math.floor(rawIndex))
      );

      Object.entries(entry.amounts).forEach(([key, amount]) => {
        const itemType = key as ItemType;
        const numericAmount = amount ?? 0;
        if (!ITEM_TYPES.includes(itemType) || numericAmount <= 0) {
          return;
        }

        const segmentRates = rates[itemType];
        segmentRates[segmentIndex] =
          (segmentRates[segmentIndex] ?? 0) + numericAmount;
        totals[itemType] += numericAmount;
      });
    });

    const metrics: Record<ItemType, ItemProductionMetrics> = {} as Record<
      ItemType,
      ItemProductionMetrics
    >;

    ITEM_TYPES.forEach(itemType => {
      const segmentRates = rates[itemType].map(segmentAmount =>
        segmentAmount > 0
          ? (segmentAmount * TICKS_PER_MINUTE) / segmentDurationTicks
          : 0
      );
      const averagePerMinute =
        windowTicks > 0
          ? (totals[itemType] * TICKS_PER_MINUTE) / windowTicks
          : 0;

      metrics[itemType] = {
        rates: segmentRates,
        averagePerMinute,
        totalAmount: totals[itemType],
      };
    });

    return metrics;
  }, [history, currentTick, selectedWindow]);

  const productionHotkeyLabel = formatHotkeyLabel(CONTROLS.PRODUCTION_MENU);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className='production-menu-overlay'
      role='dialog'
      aria-modal='true'
      aria-label='Production analytics'
    >
      <div className='production-menu'>
        <header className='production-menu__header'>
          <div>
            <h3>Production Analytics</h3>
            <p>
              Monitoring per-minute output over the last {selectedWindow} minute
              {selectedWindow > 1 ? 's' : ''}.
            </p>
          </div>
          <button
            type='button'
            className='production-menu__close'
            onClick={onClose}
            aria-label='Close production analytics'
          >
            ×
          </button>
        </header>

        <div className='production-menu__controls'>
          <span className='production-menu__controls-label'>Window:</span>
          {WINDOW_OPTIONS.map(option => (
            <button
              key={option}
              type='button'
              className={`production-menu__window-button ${
                option === selectedWindow ? 'active' : ''
              }`}
              onClick={() => setSelectedWindow(option)}
              aria-pressed={option === selectedWindow}
            >
              Last {option} min
            </button>
          ))}
          <span className='production-menu__hotkey'>
            Hotkey: {productionHotkeyLabel}
          </span>
        </div>

        <div className='production-menu__body'>
          {ITEM_TYPES.map(itemType => {
            const metrics = metricsByItem[itemType];
            const rates =
              metrics?.rates ?? Array.from({ length: SEGMENT_COUNT }, () => 0);
            const maxRate = Math.max(0, ...rates);
            const averageRate = metrics?.averagePerMinute ?? 0;
            const totalProduced = metrics?.totalAmount ?? 0;
            const itemName = ITEM_NAME_BY_TYPE[itemType] ?? itemType;

            return (
              <div className='production-menu__row' key={itemType}>
                <div className='production-menu__row-header'>
                  <span className='production-menu__item-name'>{itemName}</span>
                  <span className='production-menu__item-rate'>
                    {formatRate(averageRate)} / min
                  </span>
                </div>
                <div className='production-menu__sparkline-wrapper'>
                  <Sparkline
                    className='production-menu__sparkline'
                    values={rates}
                    maxValue={maxRate}
                    ariaLabel={`Production history for ${itemName}`}
                  />
                  <div className='production-menu__sparkline-scale'>
                    <span>0</span>
                    <span>{formatRate(maxRate)} / min</span>
                  </div>
                </div>
                <div className='production-menu__details'>
                  Produced {totalProduced} item{totalProduced === 1 ? '' : 's'}{' '}
                  in the selected window.
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { ProductionMenu };
